import { Link } from "react-router-dom";
import { CallToAction } from "@themes/pollinator/ui/CallToAction";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";
import { RotatingText } from "@themes/pollinator/ui/RotatingText";
import { MarketingSection } from "../../components/Section";
import { InviteCodeFormContainer } from "@platform/features/invitation";
import { INVITATION_TYPE, type Invitation } from "@core/features/invitation";
import type { MarketingSiteMode } from "@core/features/marketing";

const ROTATING_WORDS = [
  "change-maker",
  "technologist",
  "artist",
  "alumni",
  "curator",
  "founder",
  "innovator",
  "investor",
  "leader",
  "cultural worker",
];

export type Props = {
  onDashboardClick?: () => void;
  mode: MarketingSiteMode;
  isAuthenticated: boolean;
  invitation?: Invitation | null;
  onJoinClick?: () => void;
  isEnrolled?: boolean;
  onClaimClick?: () => void;
  isClaimLoading?: boolean;
  invitationError?: string | null;
  errorInvitation?: Invitation | null;
  /** Shortcode from the URL, used in the fallback error title */
  shortcode?: string;
  onGoBackClick?: () => void;
  onLoginClick?: () => void;
};

export function MarketingHero({
  onDashboardClick,
  mode,
  isAuthenticated,
  invitation,
  onJoinClick,
  isEnrolled,
  onClaimClick,
  isClaimLoading,
  invitationError,
  errorInvitation,
  shortcode,
  onGoBackClick,
  onLoginClick,
}: Props) {
  const isInviteOnly = mode === "invite_only";
  const isClosed = mode === "closed";
  const hasInvitation = !!invitation;
  const isOrgAdminInvitation =
    invitation?.type === INVITATION_TYPE.ORG_ADMIN ||
    invitation?.type === INVITATION_TYPE.ORG_ADMIN_ONBOARDER;
  const isErrorOrgAdmin =
    errorInvitation?.type === INVITATION_TYPE.ORG_ADMIN ||
    errorInvitation?.type === INVITATION_TYPE.ORG_ADMIN_ONBOARDER;

  return (
    <MarketingSection id="hero">
      <div className="flex flex-col gap-4 md:gap-6">
        <Type as="h1" variant="h1">
          Curated{" "}
          <RotatingText dynamicTexts={ROTATING_WORDS} intervalDuration={3} />{" "}
          cohorts for personal & professional growth.
        </Type>
        {isClosed ? (
          <Type as="p" variant="body1">
            With thousands of enrolled participants across 200+ vetted
            organizations focused on art, technology, and social impact, we are
            weaving an interdisciplinary web of mutual support.
          </Type>
        ) : (
          <Type as="p" variant="body1">
            Enrollment is open for Pollinator's next program cycle, happening in
            May of 2026.
          </Type>
        )}
      </div>

      {/* Invitation CTA: Show when a valid invitation is present */}
      {hasInvitation && (
        <CallToAction
          title={
            <>
              {isOrgAdminInvitation ? (
                <>
                  You have been invited as an Admin for{" "}
                  <span className="text-brand-100">
                    {invitation.owner_display}
                  </span>{" "}
                  in {invitation.program_name}.
                </>
              ) : (
                <>
                  <span className="text-brand-100">
                    {invitation.owner_display}
                  </span>{" "}
                  invited you to participate in{" "}
                  <span className="whitespace-nowrap">
                    {invitation.program_name}.
                  </span>
                </>
              )}
            </>
          }
          variant="platform"
          titleClassName="max-w-none"
          description={
            !isAuthenticated
              ? "Join by creating a new account or log in if you already have an account on Pollinator."
              : undefined
          }
        >
          {isAuthenticated && isEnrolled ? (
            <Button
              theme="dark"
              className="w-full md:w-fit min-w-24"
              onClick={onDashboardClick}
              data-testid="invitation-dashboard-button"
            >
              Go to Dashboard
            </Button>
          ) : isAuthenticated ? (
            <Button
              theme="dark"
              className="w-full md:w-fit min-w-24"
              onClick={onClaimClick}
              disabled={isClaimLoading}
              data-testid="invitation-claim-button"
            >
              {isClaimLoading ? "Joining…" : "Join"}
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button
                theme="dark"
                className="w-full md:w-fit min-w-24"
                onClick={onJoinClick}
                data-testid="invitation-join-button"
              >
                Join
              </Button>
              <Button
                as={Link}
                to={`/login?redirect=/invitation/${invitation.shortcode}/join`}
                theme="dark"
                variant="secondary"
                className="w-full md:w-fit min-w-24"
                data-testid="invitation-login-button"
              >
                Log In
              </Button>
            </div>
          )}
        </CallToAction>
      )}

      {/* Invitation error: Show when invitation validation failed */}
      {!hasInvitation && invitationError && (
        <CallToAction
          title={
            errorInvitation ? (
              <>
                {isErrorOrgAdmin ? (
                  <>
                    You have been invited as an Admin for{" "}
                    <span className="text-brand-100">
                      {errorInvitation.owner_display}
                    </span>{" "}
                    in {errorInvitation.program_name}.
                  </>
                ) : (
                  <>
                    <span className="text-brand-100">
                      {errorInvitation.owner_display}
                    </span>{" "}
                    invited you to participate in{" "}
                    <span className="whitespace-nowrap">
                      {errorInvitation.program_name}.
                    </span>
                  </>
                )}
              </>
            ) : (
              `Invitation ${shortcode ?? ""} is no longer available.`.trim()
            )
          }
          variant="platform"
          titleClassName="max-w-none"
          onPrimaryClick={onGoBackClick}
          primaryButtonText="Go Back"
        >
          {invitationError !== "Request failed" && (
            <Type variant="body1" className="text-error">
              {invitationError}
            </Type>
          )}
        </CallToAction>
      )}

      {/* Authenticated + no invitation: Show "Go to Dashboard" CTA */}
      {!hasInvitation && isAuthenticated && !invitationError && (
        <CallToAction
          title="Welcome back!"
          variant="platform"
          onPrimaryClick={onDashboardClick}
          primaryButtonText="Go to Dashboard"
        />
      )}

      {/* Not authenticated + invite_only + no invitation + no error: Show signup CTA with inline code form */}
      {!hasInvitation &&
        !isAuthenticated &&
        !invitationError &&
        isInviteOnly && (
          <CallToAction
            title="Enrollment is now open for our May cycle!"
            description="Artists, founders, academics, changemakers, and leaders from organizations in our network* are invited to join us for five sessions of focused cross-pollination."
            variant="platform"
            className="rounded-lg"
          >
            <InviteCodeFormContainer />
          </CallToAction>
        )}

      {/* Closed mode + not authenticated + no invitation: Show closed copy with login CTA */}
      {!hasInvitation && !isAuthenticated && !invitationError && isClosed && (
        <CallToAction
          title="Participating in the May cycle?"
          variant="platform"
          primaryButtonText="Login"
          onPrimaryClick={onLoginClick}
        />
      )}
    </MarketingSection>
  );
}

export default MarketingHero;
